import { useCallback } from 'react';
import { FixedSizeList } from 'react-window';
import { mathRound, stringToAsciiCode } from '@vfit/shared/themes';
import * as S from '../../select.style';
import { IFlatList, IGetItem } from './flatList.models';

export const FlatList = ({
  filteredItems,
  getItemKey,
  innerRef,
  isMobile,
  itemSize,
  onSelect,
  variant,
  onRequestExit,
  onLoopToInput,
  onClose,
}: IFlatList) => {
  const getItem = useCallback(
    ({ data, index, style }: IGetItem) => (
      <S.Li
        id={stringToAsciiCode(data[index])}
        onClick={(e) => {
          if (!isMobile) {
            e.preventDefault();
          } else {
            onSelect(e);
          }
        }}
        onPointerDown={(e) => {
          if (isMobile) {
            e.preventDefault();
          } else {
            onSelect(e);
          }
        }}
        role="option"
        aria-selected={innerRef.current?.value === data[index]}
        style={style}
        value={data[index]}
        variant={variant}
        tabIndex={0}
        onKeyDown={(e) => {
          const listLength = filteredItems.length;
          const isLastItem = index === listLength - 1;

          if (e.key === 'Tab') {
            onClose && onClose();
            return;
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement;
            next?.focus();
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = (e.currentTarget as HTMLElement).previousElementSibling as HTMLElement;
            if (prev) {
              prev.focus();
            } else {
              onLoopToInput && onLoopToInput();
            }
          }

          // Select item via keyboard
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Create a synthetic event-like object carrying the value attribute
            const synthetic = {
              currentTarget: {
                getAttribute: (name: string) =>
                  name === 'value' ? String((e.currentTarget as any).getAttribute('value')) : null,
              },
            } as unknown as React.MouseEvent<HTMLLIElement>;
            onSelect(synthetic);
          }
        }}
      >
        {data[index]}
      </S.Li>
    ),
    [filteredItems.length, innerRef, isMobile, onLoopToInput, onRequestExit, onSelect, variant],
  );

  return (
    <FixedSizeList
      height={
        filteredItems.length > 3
          ? mathRound(itemSize * 4)
          : mathRound(itemSize * filteredItems.length)
      }
      itemCount={filteredItems.length}
      itemSize={itemSize}
      width="100%"
      itemData={filteredItems}
      itemKey={getItemKey}
    >
      {getItem}
    </FixedSizeList>
  );
};

